import { Column, Row } from 'app/design/primitives/box';
import { Button } from 'app/design/primitives/button';
import { NothingSelectedErrorModal } from 'app/components/nothing-selected-error-modal';
import { Gradient } from 'app/design/primitives/gradient';
import { Text } from 'app/design/primitives/text';

import { SharerType } from 'app/types/preferences';
import React from 'react';
import { Platform, Pressable, StyleSheet, View } from 'react-native';
import Animated from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { InterestsComponent, InterestsStepName } from './interests-component';
import type { InterestsScreenState } from './use-interests-screen';
import { createTheme as createDSTheme } from 'app/shopify';
import type { TFunction } from 'i18next';
const theme = createDSTheme();

const AnimatedFlatList = Animated.FlatList;

interface InterestsFooterProps {
  currentIndex: number;
  currentStep: InterestsScreenState['currentStep'];
  handleContinue: () => void;
  handleSkip: () => void;
  indexShift: number;
  isComingFromBabillard: boolean;
  isLargeLayout: boolean;
  selectedTagCount: number;
  scrollToIndex: (index: number) => void;
  t: TFunction;
  useStepOverlay: boolean;
}

const DefaultInterestsFooter = ({
  currentIndex,
  currentStep,
  handleContinue,
  handleSkip,
  isComingFromBabillard,
  selectedTagCount,
  t,
}: Omit<InterestsFooterProps, 'indexShift' | 'isLargeLayout' | 'scrollToIndex' | 'useStepOverlay'>) => (
  <Column
    style={{
      gap: theme.spacing['spacing.general.x0,5'],
      marginHorizontal: theme.spacing['spacing.general.x5'],
    }}
  >
    <Button
      alignSelf="center"
      disabled={!selectedTagCount && currentStep?.stepName === InterestsStepName.ChooseTag}
      onPress={handleContinue}
      style={{ marginBottom: theme.spacing['spacing.general.x2'] }}
      variant="primary"
      width="90%"
    >
      <Text style={{ color: 'white', fontSize: 14, fontWeight: '700' }}>
        {t(`button.${currentStep?.stepName === InterestsStepName.Explain ? 'completeProfile' : 'continue'}`)}
      </Text>
    </Button>
    {!isComingFromBabillard && (currentIndex > 0 || currentStep?.stepName === InterestsStepName.Explain) ? (
      <Pressable onPress={handleSkip} style={{ alignItems: 'center', width: '100%' }}>
        <Text
          style={{
            color: theme.colors['text.primary'],
            fontWeight: '700',
            fontSize: 14,
            textDecorationLine: 'underline',
          }}
        >
          {t('button.skip')}
        </Text>
      </Pressable>
    ) : null}
  </Column>
);

const AggregateInterestsFooter = ({
  handleContinue,
  handleSkip,
  indexShift,
  isComingFromBabillard,
  isLargeLayout,
  scrollToIndex,
  t,
}: Pick<
  InterestsFooterProps,
  'handleContinue' | 'handleSkip' | 'indexShift' | 'isComingFromBabillard' | 'isLargeLayout' | 'scrollToIndex' | 't'
>) => {
  if (!isLargeLayout) {
    return (
      <Column
        style={{
          gap: theme.spacing['spacing.general.x2'],
          marginHorizontal: theme.spacing['spacing.general.x5'],
        }}
      >
        <Button alignSelf="center" onPress={handleContinue} variant="primary" width="90%">
          <Text style={{ color: 'white', fontSize: 14, fontWeight: '700' }}>{t('button.continue')}</Text>
        </Button>
        <Button alignSelf="center" onPress={() => scrollToIndex(indexShift - 1)} variant="whiteWithBorder" width="90%">
          <Text style={{ fontSize: 14, fontWeight: '700' }}>{t('button.modifyInterests')}</Text>
        </Button>
        {!isComingFromBabillard ? (
          <Pressable onPress={handleSkip} style={{ alignItems: 'center', width: '100%' }}>
            <Text
              style={{
                color: theme.colors['text.primary'],
                fontWeight: '700',
                fontSize: 14,
                textDecorationLine: 'underline',
              }}
            >
              {t('button.pass')}
            </Text>
          </Pressable>
        ) : null}
      </Column>
    );
  }
  return (
    <Row alignItems="center" style={{ marginHorizontal: theme.spacing['spacing.general.x4'], minHeight: 48 }}>
      {!isComingFromBabillard ? (
        <Pressable onPress={handleSkip}>
          <Text
            style={{
              color: theme.colors['text.primary'],
              fontWeight: '700',
              fontSize: 14,
              textDecorationLine: 'underline',
            }}
          >
            {t('button.pass')}
          </Text>
        </Pressable>
      ) : null}
      <View style={{ flex: 1 }} />
      <Row style={{ flexShrink: 0, gap: theme.spacing['spacing.general.x2'] }}>
        <Button onPress={() => scrollToIndex(indexShift - 1)} variant="secondary">
          <Text style={{ fontSize: 14, fontWeight: '700' }}>{t('button.modifyInterests')}</Text>
        </Button>
        <Button onPress={handleContinue} variant="primary">
          <Text style={{ color: 'white', fontSize: 14, fontWeight: '700' }}>{t('button.continue')}</Text>
        </Button>
      </Row>
    </Row>
  );
};

const InterestsFooter = ({
  aggregateListing,
  insetBottom,
  ...props
}: InterestsFooterProps & { aggregateListing: boolean; insetBottom: number }) => (
  <View
    style={{
      backgroundColor: theme.colors['surface.tertiary'],
      borderBottomLeftRadius: theme.borderRadii['radius.xl'],
      borderBottomRightRadius: theme.borderRadii['radius.xl'],
      bottom: 0,
      left: 0,
      paddingBottom: Math.max(theme.spacing['spacing.general.x4'], insetBottom),
      paddingTop: theme.spacing['spacing.general.x2'],
      position: 'absolute',
      right: 0,
      zIndex: 3,
    }}
  >
    {props.currentIndex > 0 && !props.useStepOverlay ? (
      <Gradient
        from={theme.colors['surface.brand.default']}
        fromOpacity={0}
        style={{
          height: 40,
          left: 0,
          pointerEvents: 'none',
          position: 'absolute',
          right: 0,
          top: -40,
          width: '100%',
        }}
        to={theme.colors['surface.tertiary']}
        toOpacity={1}
      />
    ) : null}
    {aggregateListing ? <AggregateInterestsFooter {...props} /> : <DefaultInterestsFooter {...props} />}
  </View>
);

export function InterestsWebScreenBody({ layout, state }: { layout: 'small' | 'large'; state: InterestsScreenState }) {
  const insets = useSafeAreaInsets();
  const isLargeLayout = layout === 'large';

  const {
    allTags,
    t,
    carouselItems,
    closestOffersByTag,
    currentIndex,
    currentStep,
    flatListRef,
    handleContinue,
    handleScroll,
    handleSkip,
    headerTitle,
    indexShift,
    isClosestOffersByTagFetching,
    isComingFromBabillard,
    isPopularItemsFetching,
    isTagsPending,
    itemWidth,
    popularItemsByTag,
    selectedListings,
    selectedPopularItems,
    selectedTags,
    setSelectedListings,
    setSelectedPopularItems,
    setSelectedTags,
    setShowErrorModal,
    sharerType,
    showErrorModal,
    scrollToIndex,
  } = state;

  const aggregateListing = Boolean(currentStep?.webAggregateListings);
  const useStepOverlay =
    Platform.OS === 'web' &&
    Boolean(currentStep && (aggregateListing || currentStep.stepName === InterestsStepName.ChooseTag));
  const horizontalScrollEnabled = currentIndex >= indexShift && !useStepOverlay;

  const sharedInterestsProps = {
    allTags: allTags ?? [],
    closestOffersByTag: sharerType === SharerType.borrower ? closestOffersByTag : undefined,
    currentIndex,
    isPending: isPopularItemsFetching || isClosestOffersByTagFetching,
    isTagsPending,
    onWebAggregateClose: handleSkip,
    headerTitle,
    onHeaderClose: handleSkip,
    popularItemsByTag: sharerType === SharerType.lender ? popularItemsByTag : undefined,
    scrollToIndex,
    selectedListings,
    selectedPopularItems,
    selectedTags,
    setSelectedListings,
    setSelectedPopularItems,
    setSelectedTags,
    sharerType,
  };

  return (
    <>
      <View style={{ flex: 1 }}>
        <AnimatedFlatList
          bounces
          data={carouselItems}
          decelerationRate={Platform.OS === 'ios' ? 'fast' : 'normal'}
          disableIntervalMomentum
          getItemLayout={(data, index) => ({ length: itemWidth, offset: itemWidth * index, index })}
          horizontal
          keyExtractor={(item) => item.stepName + String(item.index)}
          onScroll={handleScroll}
          pagingEnabled
          pointerEvents={useStepOverlay ? 'none' : 'auto'}
          ref={flatListRef}
          renderItem={({ item }) => (
            <Column style={{ flex: 1, minHeight: 0, width: itemWidth }}>
              <InterestsComponent {...item} {...sharedInterestsProps} />
            </Column>
          )}
          scrollEnabled={horizontalScrollEnabled}
          scrollEventThrottle={16}
          showsHorizontalScrollIndicator={false}
          style={{ flex: 1, opacity: useStepOverlay ? 0 : 1 }}
        />

        {useStepOverlay && currentStep ? (
          <View
            style={[
              StyleSheet.absoluteFill,
              {
                zIndex: 2,
                minHeight: 0,
              },
            ]}
          >
            <InterestsComponent {...currentStep} {...sharedInterestsProps} />
          </View>
        ) : null}

        <InterestsFooter
          {...{
            aggregateListing,
            currentIndex,
            currentStep,
            handleContinue,
            handleSkip,
            indexShift,
            isComingFromBabillard,
            isLargeLayout,
            scrollToIndex,
            t,
            useStepOverlay,
          }}
          insetBottom={insets.bottom}
          selectedTagCount={selectedTags.length}
        />
      </View>

      <NothingSelectedErrorModal
        buttonLabel={t('nothingSelectedError.buttonText')}
        message={t('nothingSelectedError.text')}
        onClose={() => setShowErrorModal(false)}
        open={showErrorModal}
        title={t('nothingSelectedError.title')}
      />
    </>
  );
}
